import React, { useState, useEffect } from 'react';
import SendGiftModal from './SendGiftModal';
import { getGifts } from '../../utils/apiService';

const CampaignContactTable = ({ contacts, onUpdateStatus, onRemoveContact, campaign }) => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [showGiftModal, setShowGiftModal] = useState(false);
  const [giftHistory, setGiftHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load gift history for the campaign
  const loadGiftHistory = async () => {
    if (campaign.sendosoEnabled) {
      try {
        const gifts = await getGifts(campaign._id);
        setGiftHistory(gifts);
      } catch (error) {
        console.error('Error loading gift history:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadGiftHistory();
  }, [campaign._id, campaign.sendosoEnabled]);

  // Check if a gift has been sent to a contact
  const hasGiftBeenSent = (contactId) => {
    return giftHistory.some(gift => 
      gift.alumni._id === contactId && 
      ['scheduled', 'processing', 'shipped', 'delivered'].includes(gift.status)
    );
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'contacted':
        return 'bg-blue-100 text-blue-800';
      case 'responded':
        return 'bg-green-100 text-green-800';
      case 'converted':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Handle sending a gift
  const handleSendGift = (contact) => {
    if (!hasGiftBeenSent(contact.alumni._id)) {
      setSelectedContact(contact);
      setShowGiftModal(true);
    }
  };

  // Handle gift modal close
  const handleGiftModalClose = () => {
    setShowGiftModal(false);
    setSelectedContact(null);
    // Refresh gift history after closing the modal
    loadGiftHistory();
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <h3 className="text-lg font-medium text-gray-800">Campaign Contacts</h3>
        <p className="text-sm text-gray-600">
          Target: {campaign.targetCount} contacts
        </p>
      </div>
      
      {contacts.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Contact
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Notes
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contacts.map((contact) => {
                const giftSent = hasGiftBeenSent(contact.alumni._id);
                return (
                  <tr key={contact._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{contact.alumni.emp_empname}</div>
                      <div className="text-sm text-gray-500">{contact.alumni.emp_email}</div>
                      {contact.alumni.currentCompany && (
                        <div className="text-sm text-gray-500">
                          {contact.alumni.currentTitle} at {contact.alumni.currentCompany}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={contact.status}
                        onChange={(e) => onUpdateStatus(contact._id, { status: e.target.value })}
                        className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(contact.status)}`}
                      >
                        <option value="pending">Pending</option>
                        <option value="contacted">Contacted</option>
                        <option value="responded">Responded</option>
                        <option value="converted">Converted</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(contact.lastContactDate)}
                    </td>
                    <td className="px-6 py-4">
                      <textarea
                        value={contact.notes || ''}
                        onChange={(e) => onUpdateStatus(contact._id, { notes: e.target.value })}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                        rows="2"
                        placeholder="Add notes..."
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {campaign.sendosoEnabled && (
                        <button
                          onClick={() => handleSendGift(contact)}
                          disabled={giftSent}
                          className={`inline-flex items-center ${
                            giftSent 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-blue-600 hover:text-blue-900'
                          }`}
                          title={giftSent ? 'Gift already sent' : 'Send Gift'}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
                          </svg>
                        </button>
                      )}
                      <button
                        onClick={() => onRemoveContact(contact._id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-6 text-center">
          <div className="w-16 h-16 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"></path>
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-800 mb-1">No Contacts Added Yet</h3>
          <p className="text-gray-600">Add contacts to this campaign to start tracking their progress.</p>
        </div>
      )}

      {showGiftModal && selectedContact && (
        <SendGiftModal
          contact={selectedContact}
          campaign={campaign}
          onClose={handleGiftModalClose}
        />
      )}
    </div>
  );
};

export default CampaignContactTable;
